import { useState } from 'react';
import { useOnUpdateOnly } from 'hooks/UseOnUpdateOnly';

import { searchPageApi } from 'api/searchPageApi';

import type {
  IGetAdsRequestRange,
  ISearchPageFilter,
} from 'api/types/searchPageApiTypes';
import type { ParsedUrlQuery } from 'querystring';
import type { FilterType } from 'components/SearchPage/hooks/useFilters';
import type { ResponseMakeModelFilterValue, ResponseModelFilter } from 'types';

import { mapFilterAndRangeValues } from 'helpers/seo/searchParams';
import { fireToast } from 'helpers/Toasts';
import {
  createHiddenFilters,
  findFilterByName,
} from 'components/SearchPage/mappers/filters';

export type TUseDisplayFilters = {
  section: string;
  initialFiltersData: ISearchPageFilter[];
  setFilterAndRangeValues: (
    filtersData: ISearchPageFilter[],
    filtersQueryParams?: FilterType[],
    rangeQueryParams?: IGetAdsRequestRange[],
  ) => void;
  userId?: string | string[];
  dealerId?: string;
  queryParams: ParsedUrlQuery;
  populateMakeModelFilterWithOptions: (
    filtersData: ResponseMakeModelFilterValue,
    selectedMakeModelValues?: ResponseModelFilter,
  ) => void;
};

function useDisplayFilters(props: TUseDisplayFilters) {
  const {
    initialFiltersData,
    section,
    userId,
    setFilterAndRangeValues,
    queryParams,
    populateMakeModelFilterWithOptions,
    dealerId,
  } = props;

  const FILTER_NAME = 'makeModel';

  const [filtersData, setFiltersData] = useState(initialFiltersData);

  const retrieveFilters = async () => {
    try {
      const { data } = await searchPageApi.getFilters(
        section,
        { accept: 'application/json' },
        dealerId,
      );

      const combinedfiltersData = [
        ...data.showByDefault,
        ...createHiddenFilters(),
      ];

      const { filterGroup: filters, rangeGroup: ranges } =
        mapFilterAndRangeValues(queryParams, combinedfiltersData, userId);

      const makeModelFilter = findFilterByName(
        combinedfiltersData,
        FILTER_NAME,
      );

      // Set filters with options
      setFiltersData(combinedfiltersData);
      // Set selected options if any
      setFilterAndRangeValues(combinedfiltersData, filters, ranges);

      if (
        makeModelFilter &&
        Array.isArray(makeModelFilter.values) &&
        makeModelFilter.values.length
      ) {
        const [values] = makeModelFilter.values;
        const { all, popular, valueType } = values;
        if (all && popular) {
          populateMakeModelFilterWithOptions({ all, popular, valueType });
        }
      }
    } catch (_error) {
      fireToast({
        type: 'ERROR',
        text: 'Oops! Something went wrong, please try again later',
      });
    }
  };

  useOnUpdateOnly(() => {
    retrieveFilters();
  }, [section]);

  return {
    filtersData,
    retrieveFilters,
    findFilterByName,
  };
}

export { useDisplayFilters };
